import { FC } from 'react';
import { Card, Button, Icon, Tag } from '@servicetitan/design-system';
import * as Styles from './segment.module.less';
import classNames from 'classnames';
import { Estimate as EstimateElem } from '../segment-parts/estimate';
import { Membership as MembershipElem } from '../segment-parts/membership';
import { Equipment as EquipmentElem } from '../segment-parts/equipment';
import { Customer as CustomerElem } from '../segment-parts/customer';
import { Invoice as InvoiceElem } from '../segment-parts/invoice';
import { Call as CallElem } from '../segment-parts/call';
import { Location as LocationElem } from '../segment-parts/location';
import { Job as JobElem } from '../segment-parts/job';
import { MarketingBehavior as MarketingBehaviorElem } from '../segment-parts/marketing-behavior';
import { RecurringServices as RecurringServicesElem } from '../segment-parts/recurring-services';
import { EntityFiltersExtended } from '../../utils/utils';
import { CustomFieldsFilterItem } from '@servicetitan/application-data/dist/squads/marketing';

export interface SegmentProps {
    segmentId: number;
    name: string;
    filters: EntityFiltersExtended;
    qaPrefix?: string;
    readonly?: boolean;
    locked?: boolean;
    active?: boolean;
    className?: string;
    warningText?: string;
    CustomFieldsComponent?: FC<{ type: string; filters?: CustomFieldsFilterItem[] }>;
    onSelect?(): void;
    onClose?(): void;
}

export const Segment: FC<SegmentProps> = ({
    segmentId,
    name,
    filters,
    qaPrefix,
    readonly,
    locked,
    active,
    warningText,
    className,
    onSelect,
    onClose,
    CustomFieldsComponent,
}) => {
    const {
        estimate,
        membership,
        equipment,
        customer,
        invoice,
        location,
        call,
        job,
        recurringServices,
        marketingBehavior,
        customerCustomFields,
        jobCustomFields,
        locationCustomFields,
    } = filters;

    const isCardEmpty =
        isSectionEmpty(estimate) &&
        isSectionEmpty(membership) &&
        isSectionEmpty(equipment) &&
        isSectionEmpty(customer) &&
        isSectionEmpty(location) &&
        isSectionEmpty(call) &&
        isSectionEmpty(job) &&
        isSectionEmpty(invoice) &&
        isSectionEmpty(recurringServices) &&
        isSectionEmpty(marketingBehavior) &&
        isCustomFieldsSectionEmpty(customerCustomFields?.filters) &&
        isCustomFieldsSectionEmpty(locationCustomFields?.filters) &&
        isCustomFieldsSectionEmpty(jobCustomFields?.filters);

    return (
        <Card
            onClick={readonly ? undefined : onSelect}
            className={classNames(Styles.segmentCard, className, `${qaPrefix}-segment`)}
            sharp
        >
            <Card.Section
                blue
                className={classNames(Styles.segmentCardTitle, active && Styles.active)}
            >
                <span>{name}</span>
                {locked && (
                    <Button
                        className={classNames(Styles.closeSegment, `${qaPrefix}-close-segment`)}
                    >
                        <Icon name="lock" />
                    </Button>
                )}
                {!readonly && (
                    <Button
                        className={classNames(Styles.closeSegment, `${qaPrefix}-close-segment`)}
                        onClick={onClose}
                        small
                    >
                        <Icon name="close" />
                    </Button>
                )}
            </Card.Section>
            {warningText && (
                <Tag className={Styles.segmentCardWarning} icon="warning" color="warning" subtle>
                    {warningText}
                </Tag>
            )}
            {estimate && !isSectionEmpty(estimate) && (
                <Card.Section className={Styles.segmentCardKpi}>
                    <EstimateElem estimate={estimate} />
                </Card.Section>
            )}
            {membership && !isSectionEmpty(membership) && (
                <Card.Section className={Styles.segmentCardKpi}>
                    <MembershipElem membership={membership} />
                </Card.Section>
            )}
            {equipment && !isSectionEmpty(equipment) && (
                <Card.Section className={Styles.segmentCardKpi}>
                    <EquipmentElem equipment={equipment} />
                </Card.Section>
            )}
            {((customer && !isSectionEmpty(customer)) ||
                !isCustomFieldsSectionEmpty(customerCustomFields?.filters)) && (
                <Card.Section className={Styles.segmentCardKpi}>
                    <CustomerElem
                        segmentId={segmentId}
                        customer={customer}
                        customFields={customerCustomFields}
                        CustomFieldsComponent={CustomFieldsComponent}
                    />
                </Card.Section>
            )}
            {((location && !isSectionEmpty(location)) ||
                !isCustomFieldsSectionEmpty(locationCustomFields?.filters)) && (
                <Card.Section className={Styles.segmentCardKpi}>
                    <LocationElem
                        segmentId={segmentId}
                        location={location}
                        customFields={locationCustomFields}
                        CustomFieldsComponent={CustomFieldsComponent}
                    />
                </Card.Section>
            )}
            {call && !isSectionEmpty(call) && (
                <Card.Section className={Styles.segmentCardKpi}>
                    <CallElem call={call} />
                </Card.Section>
            )}
            {((job && !isSectionEmpty(job)) ||
                !isCustomFieldsSectionEmpty(jobCustomFields?.filters)) && (
                <Card.Section className={Styles.segmentCardKpi}>
                    <JobElem
                        segmentId={segmentId}
                        job={job}
                        customFields={jobCustomFields}
                        CustomFieldsComponent={CustomFieldsComponent}
                    />
                </Card.Section>
            )}
            {invoice && !isSectionEmpty(invoice) && (
                <Card.Section className={Styles.segmentCardKpi}>
                    <InvoiceElem invoice={invoice} />
                </Card.Section>
            )}
            {recurringServices && !isSectionEmpty(recurringServices) && (
                <Card.Section className={Styles.segmentCardKpi}>
                    <RecurringServicesElem recurringServices={recurringServices} />
                </Card.Section>
            )}
            {marketingBehavior && !isSectionEmpty(marketingBehavior) && (
                <Card.Section className={Styles.segmentCardKpi}>
                    <MarketingBehaviorElem marketingBehavior={marketingBehavior} />
                </Card.Section>
            )}
            {isCardEmpty && (
                <Card.Section>
                    <span className={Styles.segmentCardEmpty}>
                        Select first category →<br />
                        −<br />
                    </span>
                </Card.Section>
            )}
        </Card>
    );
};

const isFieldEmpty = (value: any) => {
    if (value == null) {
        return true;
    }
    if (Array.isArray(value) && !value.length) {
        return true;
    }

    return false;
};

export const isSectionEmpty = (obj?: { [key: string]: any }) => {
    if (!obj) {
        return true;
    }

    for (const key in obj) {
        if (!isFieldEmpty(obj[key])) {
            return false;
        }
    }

    return true;
};

/**
 * Checks whether the given array of `CustomFieldsFilterItem` objects is empty or
 * contains only one item with an empty filter type or an empty `values` array.
 */
export const isCustomFieldsSectionEmpty = (filters?: CustomFieldsFilterItem[]) => {
    return (
        !filters ||
        filters.length === 0 ||
        filters.every(filter => !filter.filterTypeId || filter.values.length === 0)
    );
};
